// OAuth sign-in + session state (ticket 302). Device identity (device.ts)
// is untouched and keeps riding on every edge call via X-Device-Id; this is
// a separate, additive identity carried by the browser's own session cookie
// (same-origin fetch, no change needed to edgeApi.ts's `call()`).
//
// Routes reconciled against better-auth's own mounted shape (apps/edge/src/
// auth.ts's `basePath: "/v1/auth"`, better-auth v1.7's dist/api/routes/*):
// `GET /v1/auth/get-session` (not a hand-rolled "/v1/auth/session"),
// `POST /v1/auth/sign-out` (not `DELETE /v1/auth/session`), and
// `POST /v1/auth/sign-in/social` (not a hand-rolled GET "/:provider/start" --
// better-auth's sign-in is POST-only and replies with a JSON authorize URL
// to navigate to, it never serves a redirect to a plain GET). The one route
// that genuinely has no better-auth equivalent, provider-gating, is served
// by a small edge-side addition (`GET /v1/auth/providers`, auth.ts) rather
// than a better-auth route -- that part of this module is unchanged.
//
// External-store shape matches lib/theme.ts's `useTheme()` precedent:
// module-level state + a listener set + `useSyncExternalStore`.
import { useSyncExternalStore } from "react";
import { z } from "zod";
import { call, EdgeApiError } from "../edgeApi";
import { getEdgeStatus, subscribeEdgeStatus } from "../edgeStatus";
import { OAuthProvider, OAuthProviderAvailability, type SocialUserProfile } from "./account";
import { switchOwner } from "../tasting/store";
import { forgetInviteTokens, keepInviteTokensFor } from "./inviteLink";

export type { OAuthProvider, OAuthProviderAvailability, SocialUserProfile } from "./account";

/** The signed-in person's own profile always carries their email; other people's never need to. */
export type SignedInUser = SocialUserProfile & { email: string };

export type AuthState =
  | { status: "loading" }
  | { status: "signedOut" }
  | {
      status: "signedIn";
      user: SignedInUser;
      /** The last check got no answer (offline, 5xx): this is the remembered account. */
      unconfirmed?: true;
    }
  | { status: "error"; message: string };

let current: AuthState = { status: "loading" };
let inflight: Promise<AuthState> | undefined;
let stopWatching: (() => void) | undefined;
const listeners = new Set<() => void>();

function setState(next: AuthState): void {
  // Any answer about who is signed in decides whose invite links this device may keep.
  if (next.status === "signedOut") keepInviteTokensFor(undefined);
  if (next.status === "signedIn") keepInviteTokensFor(next.user.id);
  // A tasting in front of the taster belongs to whoever began it: another account does not inherit it.
  if (next.status === "signedOut") switchOwner(undefined);
  if (next.status === "signedIn") switchOwner(next.user.id);
  current = next;
  listeners.forEach((listener) => {
    listener();
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Calls `listener` on every sign-in, sign-out and session check; returns the unsubscribe. */
export const onSessionChange = subscribe;

export function getAuthState(): AuthState {
  return current;
}

/** SignIn/About read this; follows lib/theme.ts's `useTheme()` pattern. */
export function useSession(): AuthState {
  return useSyncExternalStore(subscribe, getAuthState, () => ({ status: "loading" }));
}

/** `GET /v1/auth/providers` -- lets `ProviderPicker` grey out a
 * not-yet-reviewed row (Instagram/TikTok pre-review) up front, instead of
 * discovering it only on a failed redirect. Unlike every other function in
 * this module, this hits an edge-side addition, not a better-auth route
 * (better-auth has no "is this provider reviewed" concept of its own). */
export function getProviderAvailability(): Promise<OAuthProviderAvailability> {
  return call(OAuthProviderAvailability, "/v1/auth/providers");
}

export function isProviderGated(
  availability: OAuthProviderAvailability,
  provider: OAuthProvider,
): boolean {
  return !availability[provider];
}

export type OAuthRedirectResult =
  | { kind: "success" }
  | { kind: "denied" }
  | { kind: "error"; message: string };

/**
 * Reads the query string better-auth's OAuth callback lands the browser back
 * on, once it redirects to the `callbackURL`/`errorCallbackURL` this module
 * passes through `startSignIn` below. Shape (`auth=success|denied|error`,
 * optional `message`) is this app's own choice for that callback URL, not
 * something better-auth defines -- it's whatever query string we asked for.
 * Returns `undefined` when `search` carries no `auth` param at all, so a
 * normal non-OAuth navigation never gets misread as a failed sign-in.
 */
export function parseOAuthRedirect(search: string): OAuthRedirectResult | undefined {
  const params = new URLSearchParams(search);
  const status = params.get("auth");
  if (status === null) return undefined;
  if (status === "success") return { kind: "success" };
  if (status === "denied") return { kind: "denied" };
  return { kind: "error", message: params.get("message") ?? "" };
}

/** better-auth's own `/sign-in/social` response shape (dist/api/routes/
 * sign-in.mjs): `url` is the provider's authorization URL to navigate the
 * browser to -- there is no GET redirect endpoint to build a plain href
 * from, so sign-in is necessarily this async POST-then-navigate shape. */
const SignInSocialResponse = z.object({ url: z.string().min(1) });

/**
 * Starts an OAuth sign-in: POSTs to better-auth's real `/sign-in/social`
 * endpoint and returns the provider's authorization URL for the caller to
 * navigate the browser to (`window.location.href = url`). `callbackPath`
 * becomes the `auth=success` URL `parseOAuthRedirect` reads back after the
 * provider's own consent UI and better-auth's own callback round trip.
 */
export async function startSignIn(provider: OAuthProvider, callbackPath: string): Promise<string> {
  const callbackURL = `${window.location.origin}${callbackPath}`;
  const errorCallbackURL = `${window.location.origin}${window.location.pathname}?auth=error`;
  const response = await call(SignInSocialResponse, "/v1/auth/sign-in/social", {
    method: "POST",
    body: JSON.stringify({
      provider: OAuthProvider.parse(provider),
      callbackURL,
      errorCallbackURL,
    }),
  });
  return response.url;
}

/** better-auth's own raw user shape (`name`/`image`, not this app's
 * `displayName`/`avatarUrl`) -- `GET /get-session` is forwarded verbatim
 * through the `/v1/auth/*` catch-all, so it is never remapped to
 * `UserProfile` server-side the way `GET /v1/account` is. */
const RawAuthUser = z.object({
  id: z.string().min(1),
  // Everything but the id falls back instead of failing: a payload that drifted between the
  // bundle and the edge (an update lands on one before the other) is still a valid session, and
  // refusing it would leave a signed-in person looking signed out until the shapes meet again.
  email: z.string().catch(""),
  name: z.string().catch(""),
  image: z.string().nullable().catch(null),
  // The searchable name the Samen profile shows; absent from sessions made before it existed.
  handle: z.string().nullable().optional().catch(undefined),
  // Privacy-safe when unreadable; better-auth's additionalFields (auth.ts) carry it with
  // `discoverable`, which stays found-by-default (human override #3) when absent.
  isPublic: z.boolean().catch(false),
  discoverable: z.boolean().optional().catch(undefined),
  createdAt: z.string().catch(""),
});

function toSocialUserProfile(user: z.infer<typeof RawAuthUser>): SignedInUser {
  return {
    id: user.id,
    email: user.email,
    ...(user.handle === undefined ? {} : { handle: user.handle }),
    displayName: user.name,
    avatarUrl: user.image,
    isPublic: user.isPublic,
    discoverable: user.discoverable,
    createdAt: user.createdAt,
  };
}

/** better-auth's own `/get-session` response: `{ session, user } | null` --
 * `session`'s own fields are irrelevant here, only its presence/absence. */
const GetSessionResponse = z.object({ session: z.looseObject({}), user: RawAuthUser }).nullable();

const ACCOUNT_KEY = "proef.account";
/** Read as tolerantly as the session itself: the previous bundle wrote this, and may have known fewer fields. */
const RememberedUser = z.object({
  id: z.string().min(1),
  email: z.string().catch(""),
  handle: z.string().nullable().optional().catch(undefined),
  displayName: z.string().catch(""),
  avatarUrl: z.string().nullable().catch(null),
  isPublic: z.boolean().catch(false),
  discoverable: z.boolean().optional().catch(undefined),
  createdAt: z.string().catch(""),
});

/** The account the edge last confirmed, so a reload during an edge outage still knows who this is. */
function readRememberedUser(): SignedInUser | undefined {
  try {
    const parsed = RememberedUser.safeParse(
      JSON.parse(window.localStorage.getItem(ACCOUNT_KEY) ?? "null"),
    );
    return parsed.success ? parsed.data : undefined;
  } catch {
    // Blocked storage or a damaged value: nothing remembered.
    return undefined;
  }
}

function rememberUser(user: SignedInUser | undefined): void {
  try {
    if (user === undefined) window.localStorage.removeItem(ACCOUNT_KEY);
    else window.localStorage.setItem(ACCOUNT_KEY, JSON.stringify(user));
  } catch {
    // Not persisting only means a reload during an outage shows the error state instead.
  }
}

function signedOut(): AuthState {
  rememberUser(undefined);
  return { status: "signedOut" };
}

/** A check that got no answer (offline, network error, 5xx) keeps whoever was signed in. */
function unanswered(error: unknown): AuthState {
  const known = current.status === "signedIn" ? current.user : readRememberedUser();
  if (known !== undefined) return { status: "signedIn", user: known, unconfirmed: true };
  return {
    status: "error",
    message: error instanceof EdgeApiError ? error.message : "unknown error",
  };
}

async function checkSession(): Promise<AuthState> {
  if (!navigator.onLine) return unanswered(undefined);
  try {
    const response = await call(GetSessionResponse, "/v1/auth/get-session");
    if (response === null) return signedOut();
    const user = toSocialUserProfile(response.user);
    rememberUser(user);
    return { status: "signedIn", user };
  } catch (error) {
    // Only the edge saying so ends a session: a JSON 401/403 here, or a 200 without one above.
    // A refusal that is not JSON came from something in front of the edge (a firewall, a deploy
    // window), which knows nothing about the session.
    const revoked =
      error instanceof EdgeApiError &&
      (error.status === 401 || error.status === 403) &&
      typeof error.body === "object" &&
      error.body !== null;
    return revoked ? signedOut() : unanswered(error);
  }
}

async function settleCheck(): Promise<AuthState> {
  try {
    const next = await checkSession();
    setState(next);
    return next;
  } finally {
    inflight = undefined;
  }
}

/** Calls the edge's session-check endpoint and updates the shared state every `useSession()`
 * subscriber reads. Never throws, and never signs out on a check that got no answer: the edge's
 * session refresh writes to D1, so a D1 outage must not look like a revoked session. One check
 * runs at a time, since focus, visibility and online often fire together. */
export function refreshSession(): Promise<AuthState> {
  inflight ??= settleCheck();
  return inflight;
}

function needsRecheck(): boolean {
  return (
    current.status === "error" || (current.status === "signedIn" && current.unconfirmed === true)
  );
}

/** The boot check, then a recheck at the app's next natural moments (back in view, back online,
 * the edge answering again) while the last one went unanswered -- no timer loop. Returns the stop. */
export function startSessionWatch(): () => void {
  stopWatching?.();
  const recheck = (): void => {
    if (needsRecheck()) void refreshSession();
  };
  const onVisible = (): void => {
    if (document.visibilityState === "visible") recheck();
  };
  const stopEdge = subscribeEdgeStatus(() => {
    if (getEdgeStatus() === "ok") recheck();
  });
  window.addEventListener("online", recheck);
  document.addEventListener("visibilitychange", onVisible);
  const stop = (): void => {
    stopEdge();
    window.removeEventListener("online", recheck);
    document.removeEventListener("visibilitychange", onVisible);
    if (stopWatching === stop) stopWatching = undefined;
  };
  stopWatching = stop;
  void refreshSession();
  return stop;
}

/** Back to a cold start (tests): in-memory state and listeners gone, the remembered account kept. */
export function resetSession(): void {
  stopWatching?.();
  current = { status: "loading" };
  inflight = undefined;
}

const SignOutResponse = z.object({ success: z.boolean() });

const beforeSignOut = new Set<() => Promise<void>>();

/** Work that needs the session still valid (the Academy's last flush to the account) runs before it ends. */
export function onBeforeSignOut(task: () => Promise<void>): () => void {
  beforeSignOut.add(task);
  return () => beforeSignOut.delete(task);
}

export async function signOut(): Promise<void> {
  await Promise.all([...beforeSignOut].map((task) => task().catch(() => undefined)));
  await call(SignOutResponse, "/v1/auth/sign-out", { method: "POST" });
  forgetInviteTokens();
  setState(signedOut());
}

/**
 * The provider sends the browser back to `?auth=…` on the app's path with no route in the fragment,
 * which would open the landing screen and lose the sign-in's redirect. Called before the first
 * render: it puts SignIn in front of that marker, which then finishes the round trip.
 */
export function routeOAuthReturn(): boolean {
  const { hash, pathname, search } = window.location;
  if (parseOAuthRedirect(search) === undefined || hash.startsWith("#/signin")) return false;
  window.history.replaceState(window.history.state, "", `${pathname}${search}#/signin`);
  return true;
}

/** Drops the `?auth=…` marker once read, so a later visit to SignIn does not act on it again. */
export function clearOAuthMarker(): void {
  const { hash, pathname } = window.location;
  window.history.replaceState(window.history.state, "", `${pathname}${hash}`);
}
